import { buildEvent, type SubjectType } from '@jac/shared'
import type { EventType } from '@jac/shared'
import type { Database } from '@jac/shared/generated/db-types'
import { supabase } from './supabase'

type InsertEventArgs = {
  type: EventType
  subjectType: SubjectType
  subjectId: string
  payload?: Record<string, unknown>
}

type EventRow = Database['public']['Tables']['events']['Insert']

// Fire-and-log: if the audit insert fails we keep the parent mutation because
// the DB write already landed — but we surface the error to the console so a
// broken audit trail isn't silent. True atomicity would require a stored
// procedure or an edge function; Phase 1a accepts the best-effort trade-off
// because events_insert RLS is permissive for any authenticated consultant.
export async function insertEvent(args: InsertEventArgs): Promise<void> {
  // getSession(), NOT getUser(): getUser() is a NETWORK round-trip to /auth/v1/user,
  // and this helper runs on every write path in the app — it was responsible for
  // 1,767 auth requests in 36 minutes during the 2026-09-10 incident, turning every
  // logical write into two round-trips before the insert even started. getSession()
  // reads the locally cached session (refreshing only when the token is near expiry).
  //
  // Security-neutral: `actor_id` is a column the client sends either way, so this
  // swap grants no new ability to forge it. The real authorisation is the JWT that
  // PostgREST verifies plus the events_insert RLS policy. (Proper hardening would be
  // a DB-side `default auth.uid()` / trigger so the client cannot supply actor_id at
  // all — worth doing, but out of scope here.)
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const user = session?.user ?? null
  // zod's `z.record(z.unknown())` is structurally compatible with the DB's
  // Json-typed payload column at runtime (both are JSON), but TS treats the
  // recursive Json type as strictly narrower. Cast at the boundary; the
  // EventInsert schema above has already validated the row shape.
  const row = buildEvent({
    type: args.type,
    actorId: user?.id ?? null,
    subjectType: args.subjectType,
    subjectId: args.subjectId,
    payload: args.payload,
  }) as unknown as EventRow
  const { error } = await supabase.from('events').insert(row)
  if (error) {
    console.error('[events.insert] failed — audit entry lost', {
      type: args.type,
      subjectId: args.subjectId,
      error: error.message,
    })
  }
}
